import { doGET } from "../../../../util/HttpUtil";
import { FORM_TYPE } from "../IPlushelper";
import { getExpandedFields, readValue, resolveFieldPath, shouldShowField, validateField } from "../../../../components/InputField/helper";
import _ from "lodash";
import { getBusinessTypeChecks } from "../BPrime/businessTypeUtils";

// REVIEW_EDIT_TOGGLE: Update these flags when enabling Phase 2 editing capabilities.
export const BUSINESS_REVIEW_EDIT_PERMISSIONS = {
    allowEntities: true, // Phase 2: allow business entity edits in periodic review.
    allowAdditionalIndividuals: true, // Phase 2: allow editing of non-primary individuals.
};

export const getBusinessReviewPermissions = () => BUSINESS_REVIEW_EDIT_PERMISSIONS;

// Add BusinessInformation field paths here to keep them display-only in periodic review.
const BUSINESS_INFO_READ_ONLY_FIELDS = new Set([
    // 'EIN',
    // 'Business_Registration_Country',
    // 'Business_Tax_ID',
    // 'mobilePhone',
    // 'mobileCountryCode',
    // 'landlinePhone',
    // 'landlineCountryCode',
]);

// REVIEW_EDIT_TOGGLE: Phase 2 enables all individual fields. Keep empty to avoid read-only gating.
const INDIVIDUAL_READ_ONLY_FIELDS = new Set([]);

export const getPrimaryIndividualIndex = (data = {}) => {
    if (!Array.isArray(data?.individuals)) return -1;
    const idx = data.individuals.findIndex((ind) => ind?.isPrimaryUser);
    if (idx >= 0) return idx;
    return data.individuals.length ? 0 : -1;
};

const parseArrayIndexFromPath = (resolvedFieldPath) => {
    const match = resolvedFieldPath?.match(/\[(\d+)\]/);
    return match ? Number(match[1]) : null;
};

export const isBusinessFieldEditable = ({
    data,
    resolvedFieldPath,
    arrayIndexing,
    permissions = BUSINESS_REVIEW_EDIT_PERMISSIONS,
}) => {
    if (!resolvedFieldPath) return true;

    const perms = {
        ...BUSINESS_REVIEW_EDIT_PERMISSIONS,
        ...(permissions || {}),
    };

    if (resolvedFieldPath === 'hasEntity' || resolvedFieldPath.startsWith('entities')) {
        return perms.allowEntities;
    }

    if (resolvedFieldPath.startsWith('individuals')) {
        const idx = (typeof arrayIndexing === 'number' && !Number.isNaN(arrayIndexing))
            ? arrayIndexing
            : parseArrayIndexFromPath(resolvedFieldPath);

        const primaryIndex = getPrimaryIndividualIndex(data);
        const individual = idx !== null && idx !== undefined ? data?.individuals?.[idx] : null;
        if (
            idx === primaryIndex
            && (
                resolvedFieldPath.endsWith('.email')
                || resolvedFieldPath.endsWith('.isAuthorizedUser')
                || resolvedFieldPath.endsWith('.accessType')
            )
        ) {
            return false;
        }
        if (idx !== primaryIndex && !perms.allowAdditionalIndividuals) {
            return false;
        }

        // INDIVIDUAL_READ_ONLY_FIELDS historically stores leaf keys (e.g. "email").
        // Support both leaf-key and full-path entries to avoid silent mismatches.
        const fieldKey = resolvedFieldPath.split('.').pop();
        if (INDIVIDUAL_READ_ONLY_FIELDS.has(fieldKey) || INDIVIDUAL_READ_ONLY_FIELDS.has(resolvedFieldPath)) {
            return false;
        }

        return true;
    }

    if (BUSINESS_INFO_READ_ONLY_FIELDS.has(resolvedFieldPath)) {
        return false;
    }

    return true;
};

// Row index from an expanded field path ("individuals[2].taxId" -> 2); null when the path
// has no numeric array segment. Single source of truth for row-scoped showIf, so the submit
// gate and error computation can't disagree on which row a field belongs to.
export const extractArrayIndex = (path) => {
    const match = typeof path === "string" ? path.match(/\[(\d+)\]/) : null;
    return match ? Number(match[1]) : null;
};

// Form config lists address sub-fields in a different order than they render (render is
// street-first; see User.js). The submit gate surfaces the first error in list order, so
// without this the toast can name a later field ("Building and Number") while the first
// empty box on screen is an earlier one ("Street name"). Reorder address sub-fields to
// render order; every other field keeps its config position. One place -> the gate stays
// aligned with the UI for every address block.
const ADDRESS_SUBFIELD_ORDER = ['street', 'buildingNumber', 'city', 'state', 'pinCode', 'country'];

const orderAddressSubFields = (fieldPaths = []) => {
    const addressOf = (path) => (typeof path === 'string' ? path.match(/^(.*\.address)\.([^.]+)$/) : null);
    const anchor = {};
    fieldPaths.forEach((path, i) => {
        const m = addressOf(path);
        if (m && anchor[m[1]] === undefined) anchor[m[1]] = i;
    });
    const subRank = (sub) => {
        const r = ADDRESS_SUBFIELD_ORDER.indexOf(sub);
        return r === -1 ? ADDRESS_SUBFIELD_ORDER.length : r;
    };
    return fieldPaths
        .map((path, i) => {
            const m = addressOf(path);
            return m
                ? { path, primary: anchor[m[1]], secondary: subRank(m[2]) }
                : { path, primary: i, secondary: -1 };
        })
        .sort((a, b) => (a.primary - b.primary) || (a.secondary - b.secondary))
        .map((entry) => entry.path);
};

export const getVisibleWizardFieldOrder = ({
    fields = [],
    wizard,
    data,
    skipField = () => false,
} = {}) => orderAddressSubFields(
    getExpandedFields(fields, data)
        .filter((field) => {
            // Expanded array fields (e.g. "individuals[2].taxId") must evaluate their
            // row-scoped showIf ("individuals[].isAuthorizedUser") against that same row.
            const arrayIndexing = extractArrayIndex(field?.field);
            return (
                field?.wizard === wizard
                && !skipField(field)
                && (!Array.isArray(field?.types) || ![1, 2, 3].includes(Number(data?.type)) || field.types.includes(Number(data?.type)))
                && shouldShowField(data, field?.showIf, arrayIndexing)
            );
        })
        .map((field) => field.field)
);


export const WIZARD = {
    // BusinessPeriodicReview Core Flow (Both BusinessPlus & BusinessPrime)
    EnhancedDueDiligenceContent: 'EnhancedDueDiligenceContent',
    BusinessInformation: 'BusinessInformation',
    BusinessActivity: 'BusinessActivity',
    BPlusAccountActivityDeclaration: 'BPlusAccountActivityDeclaration',
    BusinessCompliance: 'BusinessCompliance',
    Documents: 'Documents',
    SelfDeclaration: 'SelfDeclaration',

    // BusinessPrime Additional Wizards (only when formType is BusinessPrime)
    BusinessType: 'BusinessType',
    AboutBusiness: 'AboutBusiness',
    MoneyServicesBusiness: 'MoneyServicesBusiness',
    AssetsInvolvement: 'AssetsInvolvement',
    FundFamilyOffices: 'FundFamilyOffices',
    ComplianceDetails: 'ComplianceDetails',
    CorporateAccountUpgrade: 'CorporateAccountUpgrade',
    Review: 'Review',
    DocumentsPages: 'DocumentsPages',

    // Legacy/Other wizards (for compatibility)
    ReferralAndSource: 'ReferralAndSource',
    ExtraQuestions: 'ExtraQuestions',
    ApplicationDocRedo: 'ApplicationDocRedo',
    ThankYou: 'ThankYou',
}

export const WIZARD_NUM = {
    // BusinessPeriodicReview Core Flow (Both BusinessPlus & BusinessPrime)
    EnhancedDueDiligenceContent: 0,
    BusinessInformation: 1,
    BusinessActivity: 2,
    BPlusAccountActivityDeclaration: 3,
    BusinessCompliance: 4,
    Documents: 5,
    SelfDeclaration: 6,

    // BusinessPrime Additional Steps (sequential after SelfDeclaration)
    BusinessType: 7,
    AboutBusiness: 8,
    MoneyServicesBusiness: 9,
    AssetsInvolvement: 10,
    FundFamilyOffices: 11,
    ComplianceDetails: 12,
    CorporateAccountUpgrade: 13,
    Review: 14,
    DocumentsPages: 15,

    // Final steps
    ThankYou: Number.MAX_SAFE_INTEGER,

    APPLICATION_FORM_REDO: 16,
    KYC_VERIFICATION: 16,

    // Legacy/Other steps
    ExtraQuestions: 16,
    ApplicationDocRedo: 16,
}



/** 
 * Handler function for back button click following the correct BusinessPeriodicReview flow
 */
export const handlePrev = (wizard, applicationForm, formData) => {
    const isFormTypeBPrime = applicationForm?.formType === FORM_TYPE.BusinessPrime;

    // Core BusinessPeriodicReview back navigation (same for both BusinessPlus and BusinessPrime)
    switch (wizard) {
        case WIZARD.EnhancedDueDiligenceContent:
            return { wizard: WIZARD.EnhancedDueDiligenceContent };
        case WIZARD.BusinessInformation:
            return { wizard: WIZARD.EnhancedDueDiligenceContent };
        case WIZARD.BusinessActivity:
            return { wizard: WIZARD.BusinessInformation };
        case WIZARD.BPlusAccountActivityDeclaration:
            return { wizard: WIZARD.BusinessActivity };
        case WIZARD.BusinessCompliance:
            return { wizard: WIZARD.BPlusAccountActivityDeclaration };
        case WIZARD.Documents:
            return { wizard: WIZARD.BusinessCompliance };
        case WIZARD.SelfDeclaration:
            return { wizard: WIZARD.Documents };
        case WIZARD.ThankYou:
            // For BusinessPlus, go back to SelfDeclaration
            // For BusinessPrime, go back to Review
            if (isFormTypeBPrime) {
                return { wizard: WIZARD.Review };
            } else {
                return { wizard: WIZARD.SelfDeclaration };
            }
    }

    // BusinessPrime specific back navigation (only when formType is BusinessPrime)
    if (isFormTypeBPrime) {
        const { msbCheck, cryptoExchangeCheck, fundAndFamilyCheck, accountUpgradeCheck } = getBusinessTypeChecks(formData?.businesses);

        switch (wizard) {
            case WIZARD.ThankYou:
                return { wizard: WIZARD.DocumentsPages };
            case WIZARD.DocumentsPages:
                // In BusinessPrime flow, Documents comes after ComplianceDetails, so go back there
                // But if no compliance needed, go back to the last step taken
                if (cryptoExchangeCheck || fundAndFamilyCheck || msbCheck) {
                    return { wizard: WIZARD.ComplianceDetails };
                } else if (accountUpgradeCheck) {
                    return { wizard: WIZARD.CorporateAccountUpgrade };
                } else {
                    return { wizard: WIZARD.AboutBusiness };
                }
            case WIZARD.ComplianceDetails:
                if (accountUpgradeCheck) {
                    return { wizard: WIZARD.CorporateAccountUpgrade };
                } else if (fundAndFamilyCheck) {
                    return { wizard: WIZARD.FundFamilyOffices };
                } else if (cryptoExchangeCheck) {
                    return { wizard: WIZARD.AssetsInvolvement };
                } else if (msbCheck) {
                    return { wizard: WIZARD.MoneyServicesBusiness };
                }
                break;
            case WIZARD.CorporateAccountUpgrade:
                if (fundAndFamilyCheck) {
                    return { wizard: WIZARD.FundFamilyOffices };
                } else if (cryptoExchangeCheck) {
                    return { wizard: WIZARD.AssetsInvolvement };
                } else if (msbCheck) {
                    return { wizard: WIZARD.MoneyServicesBusiness };
                } else {
                    return { wizard: WIZARD.AboutBusiness };
                }
            case WIZARD.FundFamilyOffices:
                if (cryptoExchangeCheck) {
                    return { wizard: WIZARD.AssetsInvolvement };
                } else if (msbCheck) {
                    return { wizard: WIZARD.MoneyServicesBusiness };
                } else {
                    return { wizard: WIZARD.AboutBusiness };
                }
            case WIZARD.AssetsInvolvement:
                if (msbCheck || cryptoExchangeCheck) {
                    return { wizard: WIZARD.MoneyServicesBusiness };
                } else {
                    return { wizard: WIZARD.AboutBusiness };
                }
            case WIZARD.MoneyServicesBusiness:
                return { wizard: WIZARD.AboutBusiness };
            case WIZARD.AboutBusiness:
                return { wizard: WIZARD.BusinessType };
            case WIZARD.BusinessType:
                return { wizard: WIZARD.SelfDeclaration };
        }
    } else {
        // BusinessPlus flow
        if (wizard === WIZARD.ThankYou) {
            return { wizard: WIZARD.SelfDeclaration };
        }
    }

    // Default fallback
    return { wizard: WIZARD.EnhancedDueDiligenceContent };
};

/** 
 * Handler function for getting next step following the correct BusinessPeriodicReview flow
 * Flow: 
 * 1. EnhancedDueDiligenceContent
 * 2. BusinessInformation  
 * 3. BusinessActivity (with BusinessPrime regional fields if formType is BusinessPrime)
 * 4. BPlusAccountActivityDeclaration 
 * 5. BusinessCompliance
 * 6. Documents
 * 7. SelfDeclaration (includes hearAbtUs, showName from ReferralAndSource)
 * 8. BusinessPrime pages (only if formType is BusinessPrime)
 */
export const getNextStep = (wizard, applicationForm, formData, editMode = false) => {
    if (typeof applicationForm?.getNextStepForForm === "function") {
        return applicationForm.getNextStepForForm({
            wizard,
            applicationForm,
            data: formData,
            editMode,
        });
    }

    const isFormTypeBPrime = applicationForm?.formType === FORM_TYPE.BusinessPrime;

    // Core BusinessPeriodicReview flow (same for both BusinessPlus and BusinessPrime)
    switch (wizard) {
        case WIZARD.EnhancedDueDiligenceContent:
            return { wizard: WIZARD.BusinessInformation };
        case WIZARD.BusinessInformation:
            return { wizard: WIZARD.BusinessActivity };
        case WIZARD.BusinessActivity:
            return { wizard: WIZARD.BPlusAccountActivityDeclaration };
        case WIZARD.BPlusAccountActivityDeclaration:
            return { wizard: WIZARD.BusinessCompliance };
        case WIZARD.BusinessCompliance:
            return { wizard: WIZARD.Documents };
        case WIZARD.Documents:
            return { wizard: WIZARD.SelfDeclaration };
        case WIZARD.SelfDeclaration:
            // If BusinessPrime, continue to BusinessPrime flow, else finish
            if (isFormTypeBPrime) {
                return { wizard: WIZARD.BusinessType };
            } else {
                return { wizard: WIZARD.ThankYou };
            }
    }

    // BusinessPrime specific flow (only when formType is BusinessPrime)
    if (isFormTypeBPrime) {
        const { msbCheck, cryptoExchangeCheck, fundAndFamilyCheck, accountUpgradeCheck } = getBusinessTypeChecks(formData?.businesses);

        switch (wizard) {
            case WIZARD.BusinessType:
                return { wizard: WIZARD.AboutBusiness };
            case WIZARD.AboutBusiness:
                if (msbCheck || cryptoExchangeCheck) {
                    return { wizard: WIZARD.MoneyServicesBusiness };
                } else if (fundAndFamilyCheck) {
                    return { wizard: WIZARD.FundFamilyOffices };
                } else if (accountUpgradeCheck) {
                    return { wizard: WIZARD.CorporateAccountUpgrade };
                } else if (editMode) {
                    return { wizard: WIZARD.StartAgain };
                } else {
                    return { wizard: WIZARD.DocumentsPages };
                }
            case WIZARD.MoneyServicesBusiness:
                if (cryptoExchangeCheck) {
                    return { wizard: WIZARD.AssetsInvolvement };
                } else if (fundAndFamilyCheck) {
                    return { wizard: WIZARD.FundFamilyOffices };
                } else if (accountUpgradeCheck) {
                    return { wizard: WIZARD.CorporateAccountUpgrade };
                } else if (msbCheck) {
                    return { wizard: WIZARD.ComplianceDetails };
                } else if (editMode) {
                    return { wizard: WIZARD.StartAgain };
                } else {
                    return { wizard: WIZARD.DocumentsPages };
                }
            case WIZARD.AssetsInvolvement:
                if (fundAndFamilyCheck) {
                    return { wizard: WIZARD.FundFamilyOffices };
                } else if (accountUpgradeCheck) {
                    return { wizard: WIZARD.CorporateAccountUpgrade };
                } else if (cryptoExchangeCheck || fundAndFamilyCheck || msbCheck) {
                    return { wizard: WIZARD.ComplianceDetails };
                } else if (editMode) {
                    return { wizard: WIZARD.StartAgain };
                } else {
                    return { wizard: WIZARD.DocumentsPages };
                }
            case WIZARD.FundFamilyOffices:
                if (accountUpgradeCheck) {
                    return { wizard: WIZARD.CorporateAccountUpgrade };
                } else if (cryptoExchangeCheck || fundAndFamilyCheck || msbCheck) {
                    return { wizard: WIZARD.ComplianceDetails };
                } else if (editMode) {
                    return { wizard: WIZARD.StartAgain };
                } else {
                    return { wizard: WIZARD.DocumentsPages };
                }
            case WIZARD.CorporateAccountUpgrade:
                if (cryptoExchangeCheck || fundAndFamilyCheck || msbCheck) {
                    return { wizard: WIZARD.ComplianceDetails };
                } else if (editMode) {
                    return { wizard: WIZARD.StartAgain };
                } else {
                    return { wizard: WIZARD.DocumentsPages };
                }
            case WIZARD.ComplianceDetails:
                if (editMode) {
                    return { wizard: WIZARD.StartAgain };
                } else {
                    return { wizard: WIZARD.DocumentsPages };
                }
            case WIZARD.ThankYou:
                return { wizard: WIZARD.ThankYou };
        }
    }

    // Default fallback
    return { wizard: WIZARD.EnhancedDueDiligenceContent };
}

/**
 * Check if BPlusAccountActivityDeclaration should block progression 
 * Based on transaction values validation similar to IndividualPlus flow
 */
export const shouldBlockAccountActivityProgression = (data, applicationForm) => {
    // If no transaction data exists, allow progression
    if (!data) return false;

    // Check if required transaction fields are empty
    const requiredTransactionFields = [
        // 'Amount_Of_Initial_Deposit',
        'Average_Balance',
        'Number_Of_Monthly_Ach_Deposits',
        'Amount_Of_Monthly_Ach_Deposits',
        'Number_Of_Monthly_Domestic_Wire_Deposits',
        'Amount_Of_Monthly_Domestic_Wire_Deposits'
    ];

    const hasEmptyRequiredFields = requiredTransactionFields.some(field => {
        const value = data[field];
        return value === undefined || value === null || value === '';
    });

    return hasEmptyRequiredFields;
};

/**
 * Check if BusinessActivity should show BusinessPrime regional interaction fields
 */
export const shouldShowRegionalFields = (applicationForm) => {
    return applicationForm?.formType === FORM_TYPE.BusinessPrime
        || (applicationForm?.formType === FORM_TYPE.Business && [2, 3].includes(Number(applicationForm?.type)));
};

export const fetchBusinessEddData = async (formId) => {
    try {
        const response = await doGET(`/api/business/detail?id=${formId}`);
        return response.data ?? {};
    } catch (error) {
        console.log(error);
        return {};
    }
};

export const movePrimaryUserToFront = (list = []) => {
    if (!Array.isArray(list)) return [];

    try {
        const idx = list.findIndex(item => item?.isPrimaryUser);
        if (idx <= 0) return list;
        return [
            list[idx],
            ...list.slice(0, idx),
            ...list.slice(idx + 1)
        ];
    } catch (err) {
        console.error('movePrimaryUserToFront failed:', err);
        return Array.isArray(list) ? [...list] : [];
    }
};


// Given "entities[].name", return the array base path ("entities") to look up in data
export const getArrayBase = (fieldPath) => {
    const m = fieldPath.match(/^([^[]+)\[\]/);
    return m ? m[1] : null;
};

// For a field possibly containing "[]", return a list of { arrayIndexing, resolvedFieldPath } to validate.
export const expandFieldInstances = (field, data) => {
    const path = field?.field;
    if (!path) return [];

    // no array placeholder → single instance
    if (!path.includes("[]")) {
        const resolved = resolveFieldPath(path, null);
        return [{ arrayIndexing: null, resolvedFieldPath: resolved }];
    }

    // array placeholder
    const arrayBase = getArrayBase(path);
    // support nested arrays later if needed; for now we expand the first placeholder
    const arr = readValue(data, arrayBase) || [];
    const out = [];
    for (let i = 0; i < arr.length; i++) {
        out.push({
            arrayIndexing: i,
            resolvedFieldPath: resolveFieldPath(path, i),
        });
    }
    // if there are zero elements, still return a single “index 0” to catch required-at-least-one validations?
    // keep empty → nothing to validate unless UI actually renders an item
    return out;
};

// basic “is empty” used by required check (treats false as NOT empty)
export const isEmptyValue = (v) =>
    v === undefined ||
    v === null ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0);

const hasAnyError = (value) => {
    if (!value) return false;
    if (typeof value === "string") {
        return value.trim() !== "";
    }
    if (Array.isArray(value)) {
        return value.some(hasAnyError);
    }
    if (typeof value === "object") {
        return Object.values(value).some(hasAnyError);
    }
    return false;
};

export const clearErrorsForFields = ({ setErrors, fields = [], setHasError } = {}) => {
    if (typeof setErrors !== "function" || !Array.isArray(fields) || fields.length === 0) {
        return;
    }

    let snapshot = undefined;

    setErrors((prevErrors) => {
        if (!prevErrors || typeof prevErrors !== "object") {
            snapshot = prevErrors;
            return prevErrors;
        }

        const nextErrors = _.cloneDeep(prevErrors);
        let changed = false;

        fields.forEach((fieldPath) => {
            if (!fieldPath) return;
            if (_.has(nextErrors, fieldPath)) {
                _.unset(nextErrors, fieldPath);
                changed = true;
                return;
            }
            if (Object.prototype.hasOwnProperty.call(nextErrors, fieldPath)) {
                delete nextErrors[fieldPath];
                changed = true;
            }
        });

        snapshot = changed ? nextErrors : prevErrors;
        return changed ? nextErrors : prevErrors;
    });

    if (typeof setHasError === "function") {
        setHasError(hasAnyError(snapshot));
    }
};

export const calculateInitialErrors = ({ data, step, iPlusFields, setErrors, errors, shouldValidateField }) => {

    if (!Array.isArray(iPlusFields) || !step?.wizard) return;

    const initialErrors = {};
    const shownErrors = {};
    const visibleResolvedPaths = new Set();

    // only fields belonging to the current wizard step
    const stepFields = iPlusFields.filter((field) => (
        field?.wizard === step?.wizard
        && (!Array.isArray(field?.types) || ![1, 2, 3].includes(Number(data?.type)) || field.types.includes(Number(data?.type)))
    ));

    stepFields.forEach((field) => {
        // Expand [] fields into concrete instances found in data
        const instances = expandFieldInstances(field, data);
        if (instances.length === 0) return;

        instances.forEach(({ arrayIndexing, resolvedFieldPath }) => {
            // Respect showIf (with arrayIndexing passed down)
            const isVisible = shouldShowField(data, field.showIf, arrayIndexing);
            if (!isVisible) {
                // ensure any stale error for a now-hidden field is dropped later
                return;
            }
            const shouldValidate = shouldValidateField
                ? shouldValidateField(field, { arrayIndexing, resolvedFieldPath, data })
                : true;
            if (!shouldValidate) {
                return;
            }
            visibleResolvedPaths.add(resolvedFieldPath);

            // Read value with proper indexing
            const value = readValue(data, resolvedFieldPath, arrayIndexing);

            // 1) required-but-empty (false should **not** count as empty)
            if (field.required) {
                const empty = isEmptyValue(value) && value !== false;
                if (empty && !_.get(errors, resolvedFieldPath)) {
                    initialErrors[resolvedFieldPath] =
                        field?.errorTypes?.required || field?.error || "This field is required.";
                }
            }

            // 2) full field validation (regex/min/max/custom validator)
            const err = validateField(field, value, data);
            if (err) {
                shownErrors[resolvedFieldPath] = err;
            }
        });
    });


    // Compose: only keep current validation errors for visible fields (clear stale errors)
    setErrors(() => {
        const next = {};
        visibleResolvedPaths.forEach((resolvedPath) => {
            if (initialErrors[resolvedPath]) {
                next[resolvedPath] = initialErrors[resolvedPath];
                return;
            }
            if (shownErrors[resolvedPath]) {
                next[resolvedPath] = shownErrors[resolvedPath];
            }
        });
        return next;
    });

}


export const ADDRESS_TYPES = {
    REGISTERED: 'REGISTERED',
    MAILING: 'MAILING',
    PRINCIPAL: 'PRINCIPAL',
};

export const ADDRESS_LABELS = {
    [ADDRESS_TYPES.REGISTERED]: 'Registered Address of Business',
    [ADDRESS_TYPES.MAILING]: 'Mailing Address of Business',
};
